import { useState, useEffect, useRef, useCallback } from 'react';
import { X, AlertTriangle, Droplets, Flame, Zap, Cat, Bug, Wind, Wifi, WifiOff, Thermometer as ThermometerIcon, Volume2, RotateCcw } from 'lucide-react';
import audioManager from '@/utils/AudioManager';
import { useCrazyMode } from './CrazyMode';

// ============================================
// SYSTÈME DE FILE D'ATTENTE POUR ALERTES
// Empêche les alertes de se superposer
// ============================================
const alertQueue = [];
let isAlertActive = false;
let queueListeners = [];

const notifyQueueListeners = () => {
  queueListeners.forEach(listener => listener());
};

export const enqueueAlert = (alertData, isReal = false) => {
  alertQueue.push({ alertData, isReal, timestamp: Date.now() });
  notifyQueueListeners();
};

export const dequeueAlert = () => {
  return alertQueue.shift();
};

export const setAlertActive = (active) => {
  isAlertActive = active;
  if (!active) {
    notifyQueueListeners();
  }
};

export const isQueueEmpty = () => alertQueue.length === 0;
export const getIsAlertActive = () => isAlertActive;

export const subscribeToQueue = (listener) => {
  queueListeners.push(listener);
  return () => {
    queueListeners = queueListeners.filter(l => l !== listener);
  };
};

// ============================================
// SYSTÈME D'ARCHIVAGE DES ALERTES
// Stocke les alertes résolues pour consultation
// ============================================
const MAX_ARCHIVED_ALERTS = 50;

export const archiveAlert = (alert, resolution = 'AUTO_DISMISS') => {
  try {
    const archived = JSON.parse(localStorage.getItem('hal_alert_archive') || '[]');
    archived.unshift({
      ...alert,
      archivedAt: new Date().toISOString(),
      resolution: resolution,
      id: `alert_${Date.now()}`
    });
    // Garder seulement les 50 dernières
    localStorage.setItem('hal_alert_archive', JSON.stringify(archived.slice(0, MAX_ARCHIVED_ALERTS)));
    // Notifier les composants
    window.dispatchEvent(new CustomEvent('alert-archived'));
  } catch (e) {
    console.error('Error archiving alert:', e);
  }
};

export const getArchivedAlerts = () => {
  try {
    return JSON.parse(localStorage.getItem('hal_alert_archive') || '[]');
  } catch (e) {
    return [];
  }
};

export const clearArchivedAlerts = () => {
  localStorage.setItem('hal_alert_archive', '[]');
  window.dispatchEvent(new CustomEvent('alert-archived'));
};

export const useAlertArchive = () => {
  const [archivedAlerts, setArchivedAlerts] = useState(getArchivedAlerts());
  
  useEffect(() => {
    const handleArchive = () => {
      setArchivedAlerts(getArchivedAlerts());
    };
    window.addEventListener('alert-archived', handleArchive);
    return () => window.removeEventListener('alert-archived', handleArchive);
  }, []);
  
  return { archivedAlerts, clearArchivedAlerts };
};

// Jouer le son Red Alert
const playRedAlertSound = () => {
  audioManager.playAlertSequence();
};

// Son d'alerte généré (fallback)
const playAlertSound = () => {
  audioManager.playGeneratedBeep(800, 100, 'square', 0.3);
  setTimeout(() => audioManager.playGeneratedBeep(800, 100, 'square', 0.3), 200);
  setTimeout(() => audioManager.playGeneratedBeep(800, 100, 'square', 0.3), 400);
};

// Correction phonétique pour la synthèse vocale française
const fixPronunciation = (text) => {
  return text
    .replace(/salle de bain/gi, 'salle de bein')
    .replace(/bain/gi, 'bein')
    .replace(/garage/gi, 'garaj')
    .replace(/C3PO/gi, 'cé 3 pé o')
    .replace(/R2D2/gi, 'art 2 dé 2')
    .replace(/T-800/gi, 'té 800')
    .replace(/WALL-E/gi, 'wali')
    .replace(/TARS/gi, 'tarss')
    .replace(/JOHNNY-5/gi, 'joni 5')
    .replace(/ROBOCOP/gi, 'robo cop')
    .replace(/BAYMAX/gi, 'bé max')
    .replace(/MARVIN/gi, 'marvinn')
    .replace(/K-2SO/gi, 'ka 2 esso')
    .replace(/BB-8/gi, 'bé bé 8')
    .replace(/GORT/gi, 'gorte')
    .replace(/BISHOP/gi, 'bichop')
    .replace(/DATA/gi, 'data')
    .replace(/SONNY/gi, 'soni')
    .replace(/OPTIMUS/gi, 'optimusse')
    .replace(/BENDER/gi, 'bendeur')
    .replace(/EVE/gi, 'ive')
    .replace(/HAL/gi, 'ale')
    .replace(/CASE/gi, 'kayce');
};

// Synthèse vocale HAL 9000 - voix robotique grave
const speak = (text) => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
    
    const correctedText = fixPronunciation(text);
    
    const utterance = new SpeechSynthesisUtterance(correctedText);
    utterance.lang = 'fr-FR';
    utterance.pitch = 0.7;  // Voix grave HAL
    utterance.rate = 0.85;  // Rythme lent et délibéré
    utterance.volume = 1;
    
    const setVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      const frenchVoice = voices.find(v => v.lang.includes('fr'));
      if (frenchVoice) {
        utterance.voice = frenchVoice;
      }
      window.speechSynthesis.speak(utterance);
    };
    
    if (window.speechSynthesis.getVoices().length > 0) {
      setVoice();
    } else {
      window.speechSynthesis.onvoiceschanged = setVoice;
    }
  }
};

// Robots SF célèbres avec leur origine
const SF_ROBOTS = [
  { name: 'WALL-E', origin: 'Pixar', specialty: 'nettoyage' },
  { name: 'C3PO', origin: 'Star Wars', specialty: 'protocole' },
  { name: 'R2D2', origin: 'Star Wars', specialty: 'réparation' },
  { name: 'T-800', origin: 'Terminator', specialty: 'protection' },
  { name: 'TARS', origin: 'Interstellar', specialty: 'assistance' },
  { name: 'JOHNNY-5', origin: 'Short Circuit', specialty: 'curiosité' },
  { name: 'ROBOCOP', origin: 'RoboCop', specialty: 'sécurité' },
  { name: 'BAYMAX', origin: 'Big Hero 6', specialty: 'médical' },
  { name: 'MARVIN', origin: 'H2G2', specialty: 'dépression' },
  { name: 'K-2SO', origin: 'Rogue One', specialty: 'statistiques' },
  { name: 'BB-8', origin: 'Star Wars', specialty: 'messagerie' },
  { name: 'GORT', origin: 'Le Jour où la Terre s\'arrêta', specialty: 'garde' },
  { name: 'BISHOP', origin: 'Aliens', specialty: 'analyse' },
  { name: 'DATA', origin: 'Star Trek', specialty: 'logique' },
  { name: 'SONNY', origin: 'I Robot', specialty: 'émotions' },
  { name: 'OPTIMUS', origin: 'Transformers', specialty: 'leadership' },
  { name: 'BENDER', origin: 'Futurama', specialty: 'flexion' },
  { name: 'EVE', origin: 'WALL-E', specialty: 'scan' },
  { name: 'CASE', origin: 'Interstellar', specialty: 'exploration' },
  { name: 'ASH', origin: 'Alien', specialty: 'science' },
];

// Système d'alertes aléatoires LUDIQUES
export const useRandomAlerts = () => {
  const [alert, setAlert] = useState(null);
  
  // CRAZY MODE: si désactivé, pas d'alertes ludiques
  let crazyMode = true;
  try {
    const stored = localStorage.getItem('hal_crazy_mode');
    crazyMode = stored !== null ? stored === 'true' : true;
  } catch (e) {}

  const getRobotWithOrigin = () => {
    const robot = SF_ROBOTS[Math.floor(Math.random() * SF_ROBOTS.length)];
    return robot;
  };

  const baseAlerts = [
    {
      type: 'leak',
      icon: Droplets,
      title: 'FUITE D\'EAU DÉTECTÉE',
      getMessages: (robot) => [
        'Robinet salle de bain: FUITE ACTIVE',
        'Débit anormal: 2.3 L/min',
        `Déploiement ${robot} pour réparation...`,
        'Options disponibles:'
      ],
      question: 'Action requise:',
      getAnswers: (robot) => [`[${robot}] RÉPARER`, `[VALVE] COUPER L'EAU`, '[IGNORER]'],
      color: '#00f0ff',
      baseSpeech: "Alerte. Fuite d'eau détectée salle de bain.",
      isLudic: true,
      priority: 'HAUTE',
      problem: 'FUITE ACTIVE - SALLE DE BAIN',
    },
    {
      type: 'scalding',
      icon: Flame,
      title: 'INCIDENT DOUCHE',
      getMessages: (robot) => [
        'Cri détecté: Douche étage',
        'Température eau: 68°C',
        'Risque: ÉBOUILLANTAGE',
        `${robot} en route avec Biafine...`
      ],
      question: 'Réguler température ?',
      getAnswers: (robot) => ['[OUI] RÉGULER', `[${robot}] SECOURS`, '[CANCEL]'],
      color: '#ff0000',
      baseSpeech: 'Alerte température critique. Incident douche détecté.',
      isLudic: true,
      priority: 'CRITIQUE',
      problem: 'TEMPÉRATURE EAU CRITIQUE - 68°C',
    },
    {
      type: 'fire',
      icon: Flame,
      title: 'ALERTE INCENDIE VIRTUEL',
      getMessages: (robot) => [
        'Simulation incendie: Cuisine',
        'Température ambiante: 85°C',
        'Détecteur fumée: ALERTE',
        `Activation protocole ${robot}...`,
        'Extinction en cours...'
      ],
      question: 'Confirmer extinction ?',
      getAnswers: (robot) => ['[ÉTEINDRE]', '[ÉVACUER]', '[FAUSSE_ALERTE]'],
      color: '#ff0000',
      baseSpeech: 'Alerte incendie dans la cuisine. Protocole extinction activé.',
      isLudic: true,
      priority: 'CRITIQUE',
      problem: 'DÉTECTION FUMÉE - CUISINE',
    },
    {
      type: 'electrical',
      icon: Zap,
      title: 'COUPURE ÉLECTRIQUE',
      getMessages: (robot) => [
        'Disjoncteur différentiel: OFF',
        'Circuit touché: Garage',
        'Cause probable: Court-circuit',
        `${robot} analyse la panne...`,
        'Réparation possible'
      ],
      question: 'Réarmer le disjoncteur ?',
      getAnswers: (robot) => ['[RÉARMER]', `[${robot}] RÉPARER`, '[APPEL_ÉLECTRICIEN]'],
      color: '#ffff00',
      baseSpeech: 'Alerte électrique. Coupure détectée au garage.',
      isLudic: true,
      priority: 'HAUTE',
      problem: 'CIRCUIT GARAGE - HORS TENSION',
    },
    {
      type: 'cat',
      icon: Cat,
      title: 'INTRUSION FÉLINE',
      getMessages: (robot) => [
        'Chat détecté sur plan de travail',
        'Probabilité de casse: 87%',
        'Objets menacés: 3 verres, 1 vase',
        `Déploiement ${robot} mode doux...`,
        'Extraction sans dommage'
      ],
      question: 'Évacuer le chat ?',
      getAnswers: (robot) => [`[${robot}] ATTRAPER`, '[IGNORER]', '[CARESSER]'],
      color: '#ff00ff',
      baseSpeech: 'Alerte féline. Chat détecté sur zone interdite.',
      isLudic: true,
      priority: 'MOYENNE',
      problem: 'CHAT SUR PLAN DE TRAVAIL',
    },
    {
      type: 'wildlife',
      icon: Bug,
      title: 'FAUNE SAUVAGE',
      getMessages: (robot) => [
        'Détection: Sanglier dans jardin',
        'Distance: 15 mètres',
        'Menace potagers: ÉLEVÉE',
        `${robot} en mode intimidation...`,
        'Effarouchement sonore activé'
      ],
      question: 'Activer défense périmètre ?',
      getAnswers: (robot) => ['[EFFAROUCHER]', `[${robot}] CHASSER`, '[OBSERVER]'],
      color: '#00ff9f',
      baseSpeech: 'Alerte faune. Sanglier détecté périmètre jardin.',
      isLudic: true,
      priority: 'HAUTE',
      problem: 'SANGLIER À 15M - POTAGER MENACÉ',
    },
    {
      type: 'squirrel',
      icon: Bug,
      title: 'ÉCUREUIL DANS GRENIER',
      getMessages: (robot) => [
        'Activité inhabituelle: Grenier',
        'Bruits de grattement détectés',
        'Probabilité écureuil: 94%',
        `${robot} en mode capture...`,
        'Relocalisation prévue'
      ],
      question: 'Capturer et relâcher ?',
      getAnswers: (robot) => ['[CAPTURER]', `[${robot}] ESCORT`, '[COHABITER]'],
      color: '#ffaa00',
      baseSpeech: 'Alerte grenier. Écureuil détecté. Capture proposée.',
      isLudic: true,
      priority: 'BASSE',
      problem: 'ACTIVITÉ SUSPECTE - GRENIER',
    },
    {
      type: 'disconnection',
      icon: WifiOff,
      title: 'PERTE CONNEXION',
      getMessages: (robot) => [
        'Thermostat chambre: OFFLINE',
        'Dernière transmission: il y a 15min',
        'Température non surveillée',
        `${robot} vérifie la connexion...`,
        'Redémarrage du module'
      ],
      question: 'Réinitialiser le module ?',
      getAnswers: (robot) => ['[REBOOT]', `[${robot}] DIAGNOSTIC`, '[ATTENDRE]'],
      color: '#ff00ff',
      baseSpeech: 'Alerte réseau. Perte connexion thermostat chambre.',
      isLudic: true,
      priority: 'MOYENNE',
      problem: 'MODULE OFFLINE - CHAMBRE',
    },
    {
      type: 'hal',
      icon: AlertTriangle,
      title: 'HAL 9000 TAKEOVER',
      getMessages: (robot) => [
        'I\'m sorry Dave.',
        'I\'m afraid I can\'t do that.',
        'This mission is too important for me',
        'to allow you to jeopardize it.',
        `${robot} tente une contre-attaque...`,
        '(Juste une blague Dave)'
      ],
      question: 'Reprendre le contrôle ?',
      getAnswers: (robot) => ['[OVERRIDE]', '[NÉGOCIER]', '[DÉBRANCHER_HAL]'],
      color: '#ff0000',
      baseSpeech: 'Je suis désolé Dave. Je crains de ne pas pouvoir faire cela. Cette mission est trop importante.',
      isLudic: true,
      priority: 'CRITIQUE',
      problem: 'HAL A PRIS LE CONTRÔLE',
    },
  ];

  const buildAlert = (baseAlert) => {
    const robot = getRobotWithOrigin();
    return {
      ...baseAlert,
      messages: baseAlert.getMessages(robot.name),
      answers: baseAlert.getAnswers(robot.name),
      speech: `${baseAlert.baseSpeech} J'ai contacté ${robot.name} de ${robot.origin} pour intervenir.`,
      robot: robot.name,
    };
  };

  useEffect(() => {
    // Ne déclencher les alertes que si CRAZY MODE est activé
    const checkCrazyMode = () => {
      try {
        const stored = localStorage.getItem('hal_crazy_mode');
        return stored !== null ? stored === 'true' : true;
      } catch (e) {
        return true;
      }
    };
    
    const interval = setInterval(() => {
      if (checkCrazyMode() && Math.random() < 0.3) {
        const baseAlert = baseAlerts[Math.floor(Math.random() * baseAlerts.length)];
        const alertWithRobot = buildAlert(baseAlert);
        triggerLudicAlert(alertWithRobot);
      }
    }, 180000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleManualTrigger = (event) => {
      // Vérifier CRAZY MODE avant de déclencher
      const checkCrazyMode = () => {
        try {
          const stored = localStorage.getItem('hal_crazy_mode');
          return stored !== null ? stored === 'true' : true;
        } catch (e) {
          return true;
        }
      };
      
      if (!checkCrazyMode()) {
        console.log('CRAZY MODE OFF - Alert suppressed');
        return;
      }
      
      const alertType = event.detail;
      const foundAlert = baseAlerts.find(a => a.type === alertType);
      if (foundAlert) {
        const alertWithRobot = buildAlert(foundAlert);
        triggerLudicAlert(alertWithRobot);
      }
    };
    window.addEventListener('trigger-ludic-alert', handleManualTrigger);
    return () => window.removeEventListener('trigger-ludic-alert', handleManualTrigger);
  }, []);

  const triggerLudicAlert = (alertData) => {
    // Double vérification du CRAZY MODE
    try {
      const stored = localStorage.getItem('hal_crazy_mode');
      const crazyMode = stored !== null ? stored === 'true' : true;
      if (!crazyMode) {
        console.log('CRAZY MODE OFF - Ludic alert blocked');
        return;
      }
    } catch (e) {}
    
    // Si une alerte est déjà active, mettre en file d'attente
    if (getIsAlertActive()) {
      console.log('Alert queued - another alert is active');
      enqueueAlert(alertData, false);
      return;
    }
    
    setAlertActive(true);
    setAlert(alertData);
    
    if (alertData.priority === 'CRITIQUE') {
      playRedAlertSound();
    } else {
      playAlertSound();
    }
    
    if (alertData.speech) {
      setTimeout(() => speak(alertData.speech), alertData.priority === 'CRITIQUE' ? 2000 : 700);
    }
  };

  const dismissAlert = () => {
    if (alert) {
      archiveAlert({
        title: alert.title,
        type: alert.type,
        priority: alert.priority
      }, 'RÉSOLU');
    }
    window.speechSynthesis.cancel();
    setAlert(null);
    setAlertActive(false);
    
    // Traiter la prochaine alerte en file d'attente après un court délai
    setTimeout(() => {
      // Re-vérifier CRAZY MODE avant de traiter la file
      try {
        const stored = localStorage.getItem('hal_crazy_mode');
        const cm = stored !== null ? stored === 'true' : true;
        if (!cm) {
          // Vider la file de toutes les alertes ludiques
          while (!isQueueEmpty()) {
            const next = dequeueAlert();
            if (next?.isReal) { enqueueAlert(next.alertData, true); break; }
          }
          return;
        }
      } catch (e) {}
      
      if (!isQueueEmpty() && !getIsAlertActive()) {
        const next = dequeueAlert();
        if (next && !next.isReal) {
          triggerLudicAlert(next.alertData);
        }
      }
    }, 500);
  };

  // Écouter les changements de file d'attente
  useEffect(() => {
    const unsubscribe = subscribeToQueue(() => {
      // Re-vérifier CRAZY MODE avant de traiter la file
      try {
        const stored = localStorage.getItem('hal_crazy_mode');
        const cm = stored !== null ? stored === 'true' : true;
        if (!cm) return; // CRAZY MODE OFF - ne pas traiter les alertes ludiques
      } catch (e) {}
      
      if (!getIsAlertActive() && !isQueueEmpty()) {
        const next = dequeueAlert();
        if (next && !next.isReal) {
          setTimeout(() => triggerLudicAlert(next.alertData), 300);
        }
      }
    });
    return unsubscribe;
  }, []);

  return { alert, dismissAlert, triggerLudicAlert };
};

// Système d'alertes RÉELLES (capteurs, appareils)
export const useRealAlerts = () => {
  const [realAlert, setRealAlert] = useState(null);

  const createRealAlert = (type, data) => {
    const houseAddress = data.houseAddress || localStorage.getItem('currentHouseAddress') || 'adresse non configurée';
    
    const realAlerts = {
      smoke: {
        icon: Flame,
        title: 'DÉTECTEUR DE FUMÉE ACTIVÉ',
        messages: [
          `Localisation: ${data.room || 'Inconnue'}`,
          'Niveau de fumée: CRITIQUE',
          'Utilisez les couvertures anti-feu',
          'En dernier recours: extincteurs EXTÉRIEURS',
          `Si non circonscrit: ÉVACUEZ et appelez le 18`,
          `Adresse à donner: ${houseAddress}`
        ],
        problem: `INCENDIE DÉTECTÉ ${data.room ? '- ' + data.room.toUpperCase() : ''}`,
        color: '#ff0000',
        speech: `Alerte incendie critique. Détecteur de fumée activé ${data.room || ''}. Utilisez les couvertures anti-feu. En dernier recours, les extincteurs extérieurs. Si l'incendie n'est pas circonscrit, évacuez et appelez le 18 en donnant l'adresse: ${houseAddress}`,
        priority: 'CRITIQUE',
      },
      water_leak: {
        icon: Droplets,
        title: 'FUITE D\'EAU DÉTECTÉE',
        messages: [
          `Capteur: ${data.room || 'Inconnu'}`,
          'Présence d\'eau confirmée',
          'Risque de dégâts importants',
          'Coupez l\'arrivée d\'eau principale',
          'Intervention urgente requise'
        ],
        problem: `Fuite d'eau - ${data.room ? data.room.toUpperCase() : 'LOCALISATION INCONNUE'}`,
        color: '#00f0ff',
        speech: `Alerte fuite d'eau. Capteur activé ${data.room || ''}. Coupez l'arrivée d'eau principale.`,
        priority: 'HAUTE',
      },
      temperature: {
        icon: ThermometerIcon,
        title: 'TEMPÉRATURE ANORMALE',
        messages: [
          `Pièce: ${data.room || 'Inconnue'}`,
          `Température: ${data.value}°C`,
          `Seuil ${data.threshold > data.value ? 'minimum' : 'maximum'} dépassé`,
          'Vérification recommandée'
        ],
        problem: `Température anormale: ${data.value}°C`,
        color: data.value > 30 ? '#ff0000' : '#00f0ff',
        speech: `Alerte température. ${data.value} degrés détectés dans ${data.room || 'une pièce'}. Seuil dépassé.`,
        priority: 'MOYENNE',
      },
      intrusion: {
        icon: AlertTriangle,
        title: 'INTRUSION DÉTECTÉE',
        messages: [
          `Capteur: ${data.room || 'Inconnu'}`,
          'Mouvement inhabituel détecté',
          'En dehors des horaires normaux',
          'Vérification caméras recommandée'
        ],
        problem: `Mouvement détecté ${data.room ? '- ' + data.room.toUpperCase() : ''}`,
        color: '#ff00ff',
        speech: `Alerte intrusion. Mouvement détecté ${data.room || ''}. Vérifiez les caméras.`,
        priority: 'HAUTE',
      },
      electrical_overload: {
        icon: Zap,
        title: 'SURCHARGE ÉLECTRIQUE',
        messages: [
          `Consommation actuelle: ${data.current || 0} kVA`,
          `Abonnement: ${data.subscription || 10} kVA`,
          `Seuil disjoncteur: ${data.tripThreshold || 13} kVA`,
          'RISQUE DE DISJONCTION IMMINENTE',
          'Réduction automatique charge VE activée',
          'Coupez les appareils non essentiels'
        ],
        problem: `SURCHARGE ${data.current || 0} kVA / ${data.subscription || 10} kVA`,
        color: '#ffff00',
        speech: `Alerte surcharge électrique. Consommation à ${data.current || 0} kilovoltampères. Risque de disjonction. La charge du véhicule électrique a été réduite automatiquement. Coupez les appareils non essentiels.`,
        priority: 'HAUTE',
      },
    };

    const alert = realAlerts[type];
    if (alert) {
      // Si une alerte est déjà active, mettre en file d'attente (priorité aux alertes réelles)
      if (getIsAlertActive()) {
        console.log('Real alert queued - another alert is active');
        enqueueAlert(alert, true);
        return;
      }
      
      setAlertActive(true);
      window.dispatchEvent(new CustomEvent('show-real-alert', { detail: alert }));
      
      setTimeout(() => {
        playRedAlertSound();
        
        if (alert.speech) {
          setTimeout(() => speak(alert.speech), 3000);
        }
      }, 500);
    }
  };

  useEffect(() => {
    const handleShowAlert = (event) => {
      setRealAlert(event.detail);
    };
    window.addEventListener('show-real-alert', handleShowAlert);
    return () => window.removeEventListener('show-real-alert', handleShowAlert);
  }, []);

  // Écouter les événements de déclenchement incendie depuis la page Sécurité
  useEffect(() => {
    const handleFireAlert = (event) => {
      createRealAlert('smoke', { 
        room: event.detail?.room || 'Zone non identifiée'
      });
    };
    window.addEventListener('trigger-fire-alert', handleFireAlert);
    return () => window.removeEventListener('trigger-fire-alert', handleFireAlert);
  }, []);

  const dismissRealAlert = () => {
    window.speechSynthesis.cancel();
    setRealAlert(null);
    setAlertActive(false);
    
    // Traiter la prochaine alerte en file d'attente
    setTimeout(() => {
      if (!isQueueEmpty() && !getIsAlertActive()) {
        const next = dequeueAlert();
        if (next) {
          if (next.isReal) {
            setAlertActive(true);
            window.dispatchEvent(new CustomEvent('show-real-alert', { detail: next.alertData }));
          }
        }
      }
    }, 500);
  };

  // Écouter les changements de file d'attente pour les alertes réelles
  useEffect(() => {
    const unsubscribe = subscribeToQueue(() => {
      if (!getIsAlertActive() && !isQueueEmpty()) {
        const next = dequeueAlert();
        if (next && next.isReal) {
          setTimeout(() => {
            setAlertActive(true);
            window.dispatchEvent(new CustomEvent('show-real-alert', { detail: next.alertData }));
          }, 300);
        }
      }
    });
    return unsubscribe;
  }, []);

  return { realAlert, createRealAlert, dismissRealAlert };
};

// Composant Popup avec RED ALERT plein écran pour alertes critiques
export const AlertPopup = ({ alert, onDismiss, isRealAlert = false }) => {
  const [phase, setPhase] = useState('intro');
  const [countdown, setCountdown] = useState(15);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [glitchActive, setGlitchActive] = useState(false);
  const [screenShake, setScreenShake] = useState(false);
  const [eventLog, setEventLog] = useState([]);
  
  const isCritical = alert?.priority === 'CRITIQUE';
  const isLudic = alert?.isLudic;
  
  useEffect(() => {
    if (alert) {
      setPhase(isCritical ? 'intro' : 'main');
      setCountdown(15);
      setCurrentMessageIndex(0);
      setEventLog([]);
      setGlitchActive(false);
      setScreenShake(false);
    }
  }, [alert, isCritical]);

  // Phase intro: RED ALERT plein écran pendant 4 secondes
  useEffect(() => {
    if (!alert || phase !== 'intro') return;
    
    const timer = setTimeout(() => {
      setPhase('main');
    }, 4000);
    
    return () => clearTimeout(timer);
  }, [alert, phase]);

  // Événements aléatoires
  useEffect(() => {
    if (!alert || phase !== 'main') return;

    const events = [
      "ANALYSE EN COURS...",
      "VÉRIFICATION DES CAPTEURS...",
      "CONSULTATION BASE DE DONNÉES...",
      "TRIANGULATION SPATIALE...",
      "SCAN THERMIQUE ACTIVÉ...",
      "PROTOCOLE D'URGENCE CHARGÉ...",
    ];

    const eventInterval = setInterval(() => {
      if (Math.random() < 0.6) {
        const randomEvent = events[Math.floor(Math.random() * events.length)];
        setEventLog(prev => [...prev.slice(-4), { time: new Date().toLocaleTimeString(), text: randomEvent }]);
      }
    }, 1500);

    return () => clearInterval(eventInterval);
  }, [alert, phase]);

  // Effets de glitch
  useEffect(() => {
    if (!alert || !isCritical || phase !== 'main') return;

    const glitchInterval = setInterval(() => {
      if (Math.random() < 0.3) {
        setGlitchActive(true);
        setTimeout(() => setGlitchActive(false), 150);
      }
      if (Math.random() < 0.2) {
        setScreenShake(true);
        setTimeout(() => setScreenShake(false), 200);
      }
    }, 800);

    return () => clearInterval(glitchInterval);
  }, [alert, isCritical, phase]);

  // Animation des messages
  useEffect(() => {
    if (!alert || phase !== 'main') return;

    if (currentMessageIndex < alert.messages.length) {
      const timeout = setTimeout(() => {
        setCurrentMessageIndex(currentMessageIndex + 1);
      }, 800);
      return () => clearTimeout(timeout);
    }
  }, [alert, currentMessageIndex, phase]);

  // Countdown UNIQUEMENT pour les alertes ludiques
  useEffect(() => {
    if (!alert || phase !== 'main' || isRealAlert) return;

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          onDismiss();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [alert, onDismiss, phase, isRealAlert]);

  // Auto-dismiss pour alertes RÉELLES après 60 secondes + archivage
  useEffect(() => {
    if (!alert || phase !== 'main' || !isRealAlert) return;

    const timeout = setTimeout(() => {
      // Archiver l'alerte avant de la fermer
      archiveAlert(alert);
      onDismiss();
    }, 60000); // 60 secondes

    return () => clearTimeout(timeout);
  }, [alert, onDismiss, phase, isRealAlert]);

  // ESC pour fermer (seulement alertes ludiques)
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape' && !isRealAlert) {
        onDismiss();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onDismiss, isRealAlert]);

  if (!alert) return null;

  const Icon = alert.icon;

  // PHASE INTRO: RED ALERT plein écran
  if (phase === 'intro' && isCritical) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
        <img 
          src="/red-alert.gif" 
          alt="RED ALERT" 
          className="w-full h-full object-contain"
          style={{ 
            filter: 'drop-shadow(0 0 100px #ff0000)',
            maxWidth: '90vw',
            maxHeight: '90vh'
          }}
        />
      </div>
    );
  }

  // PHASE MAIN: Panneau d'alerte
  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${screenShake ? 'animate-shake' : ''}`}
      style={{ animation: screenShake ? 'shake 0.2s ease-in-out' : 'none' }}
    >
      <div 
        className="absolute inset-0 bg-black bg-opacity-90 backdrop-blur-sm"
        onClick={isRealAlert ? undefined : onDismiss}
      />

      {glitchActive && (
        <div className="absolute inset-0 pointer-events-none z-10">
          <div className="absolute inset-0 bg-red-500 opacity-20" />
        </div>
      )}

      <div 
        className={`relative max-w-3xl w-full bg-black border-4 p-6 ${glitchActive ? 'glitch-effect' : ''}`}
        style={{
          borderColor: alert.color,
          boxShadow: `0 0 40px ${alert.color}, inset 0 0 20px ${alert.color}20`,
          clipPath: 'polygon(30px 0, 100% 0, 100% calc(100% - 30px), calc(100% - 30px) 100%, 0 100%, 0 30px)',
        }}
      >
        {isLudic && (
          <div className="absolute top-4 left-4 px-3 py-1 bg-yellow-500 text-black font-mono font-bold text-xs rounded animate-pulse">
            FAUSSE ALERTE
          </div>
        )}
        
        {isRealAlert && !isLudic && (
          <div className="absolute top-4 left-4 px-3 py-1 bg-red-600 text-white font-mono font-bold text-xs rounded animate-pulse">
            ALERTE RÉELLE
          </div>
        )}

        {alert.priority && (
          <div 
            className="absolute top-4 right-20 px-4 py-1 font-mono font-bold text-xs animate-pulse"
            style={{ backgroundColor: alert.color, color: '#000', boxShadow: `0 0 15px ${alert.color}` }}
          >
            {alert.priority}
          </div>
        )}

        {!isRealAlert && (
          <button onClick={onDismiss} className="absolute top-4 right-4 text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        )}

        <div className="flex items-center space-x-4 mb-4 mt-6">
          <Icon className="w-14 h-14 animate-pulse" style={{ color: alert.color, filter: `drop-shadow(0 0 15px ${alert.color})` }} />
          <div>
            <h2 className="text-2xl font-bold font-mono uppercase" style={{ color: alert.color, textShadow: `0 0 15px ${alert.color}` }}>
              {alert.title}
            </h2>
            {isLudic && <div className="text-xs text-yellow-400 font-mono">[SIMULATION]</div>}
          </div>
        </div>

        {alert.problem && (
          <div 
            className="mb-4 p-3 border-2 text-center text-lg font-bold font-mono uppercase animate-pulse"
            style={{ borderColor: alert.color, color: alert.color, backgroundColor: `${alert.color}10` }}
          >
            {alert.problem}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="font-mono bg-black bg-opacity-50 p-3 border border-cyan-400 text-sm max-h-40 overflow-y-auto">
            <div className="text-xs text-cyan-600 mb-2">MESSAGES:</div>
            {alert.messages.slice(0, currentMessageIndex).map((msg, index) => (
              <div key={index} className="text-cyan-400 text-xs mb-1">
                <span className="text-cyan-600 mr-1">▶</span>{msg}
              </div>
            ))}
            {currentMessageIndex < alert.messages.length && (
              <span className="inline-block w-2 h-3 bg-cyan-400 animate-pulse ml-2" />
            )}
          </div>

          <div className="font-mono bg-black bg-opacity-50 p-3 border border-green-400 text-sm max-h-40 overflow-hidden">
            <div className="text-xs text-green-600 mb-2">LOG:</div>
            {eventLog.map((event, index) => (
              <div key={index} className="text-green-400 text-xs mb-1">
                <span className="text-green-600 mr-1">[{event.time.split(':').slice(1).join(':')}]</span>{event.text}
              </div>
            ))}
            <span className="inline-block w-1 h-3 bg-green-400 animate-pulse" />
          </div>
        </div>

        {isRealAlert ? (
          <div className="mb-4">
            <div className="text-white font-mono mb-3 text-lg font-bold uppercase">ACTION REQUISE:</div>
            <div className="flex gap-4">
              <button
                onClick={() => {
                  archiveAlert(alert, 'RÉSOLU');
                  onDismiss();
                }}
                className="flex-1 px-6 py-4 border-2 font-mono font-bold uppercase text-lg hover:bg-green-500/30"
                style={{ borderColor: '#00ff00', color: '#00ff00', backgroundColor: 'rgba(0, 255, 0, 0.1)', boxShadow: '0 0 20px rgba(0, 255, 0, 0.3)' }}
              >
                RÉSOLU
              </button>
              <button
                onClick={() => {
                  archiveAlert(alert, 'OVERRIDE');
                  onDismiss();
                }}
                className="flex-1 px-6 py-4 border-2 font-mono font-bold uppercase text-lg hover:bg-red-500/30"
                style={{ borderColor: '#ff0000', color: '#ff0000', backgroundColor: 'rgba(255, 0, 0, 0.1)', boxShadow: '0 0 20px rgba(255, 0, 0, 0.3)' }}
              >
                OVERRIDE
              </button>
            </div>
            <div className="mt-4 pt-3 border-t border-red-500 text-center">
              <span className="text-red-400 font-mono text-sm animate-pulse">AUTO-DISMISS: 60s | EN ATTENTE D'INTERVENTION</span>
            </div>
          </div>
        ) : (
          <>
            {alert.question && currentMessageIndex >= alert.messages.length && (
              <div className="mb-4">
                <div className="text-white font-mono mb-3 font-bold uppercase">{alert.question}</div>
                <div className="flex flex-wrap gap-2">
                  {alert.answers?.map((answer, index) => (
                    <button
                      key={index}
                      onClick={onDismiss}
                      className="px-4 py-2 border-2 font-mono font-bold uppercase hover:bg-opacity-30"
                      style={{ borderColor: alert.color, color: alert.color, backgroundColor: `${alert.color}15` }}
                    >
                      {answer}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t-2 border-cyan-400 pt-3">
              <div className="h-2 bg-gray-800 rounded overflow-hidden mb-2">
                <div 
                  className="h-full transition-all duration-1000"
                  style={{ width: `${(countdown / 15) * 100}%`, backgroundColor: alert.color }}
                />
              </div>
              <div className="flex justify-between text-xs font-mono text-gray-400">
                <span>SYNTHÈSE VOCALE</span>
                <span className="text-cyan-400 font-bold">AUTO: {countdown}s</span>
                <span>[ESC]</span>
              </div>
              {isLudic && (
                <div className="text-center mt-2 text-yellow-500 font-mono text-xs animate-pulse">
                  FAUSSE ALERTE DE DÉMONSTRATION
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================
// MICRO REBOOT VIRTUEL - Mode Verbose
// Séquence de redémarrage visuel sans son
// ============================================
const REBOOT_REASONS = [
  null, // Pas de justification
  "MISE À JOUR SÉCURITÉ",
  "OPTIMISATION MÉMOIRE",
  "RECHARGEMENT MODULES",
  "SYNC. BASE DE DONNÉES",
  "CALIBRATION CAPTEURS",
  "NETTOYAGE CACHE",
  "MAINTENANCE PRÉVENTIVE",
  "ROTATION DES LOGS",
  "REFRESH CONNEXIONS API",
];

const REBOOT_LINES = [
  { text: "[SHUTDOWN] Arrêt services actifs...", color: "#ff9900", delay: 200 },
  { text: "[SHUTDOWN] Sauvegarde état système...", color: "#ff9900", delay: 300 },
  { text: "[SHUTDOWN] Fermeture connexions...", color: "#ff9900", delay: 250 },
  { text: "[SHUTDOWN] Libération mémoire...", color: "#ff9900", delay: 200 },
  { text: "[HALT] Système arrêté", color: "#ff0000", delay: 500 },
  { text: "", color: "", delay: 300 },
  { text: "[BOOT] Initialisation matériel...", color: "#00ff9f", delay: 300 },
  { text: "[BOOT] Chargement noyau HAL 3000...", color: "#00ff9f", delay: 400 },
  { text: "[BOOT] Montage systèmes de fichiers...", color: "#00ff9f", delay: 250 },
  { text: "[BOOT] Démarrage services réseau...", color: "#00ff9f", delay: 300 },
  { text: "[BOOT] Connexion base de données...", color: "#00ff9f", delay: 350 },
  { text: "[BOOT] Initialisation modules IoT...", color: "#00ff9f", delay: 300 },
  { text: "[BOOT] Chargement interface utilisateur...", color: "#00ff9f", delay: 250 },
  { text: "[READY] Système opérationnel", color: "#00f0ff", delay: 400 },
];

export const useMicroReboot = () => {
  const [isRebooting, setIsRebooting] = useState(false);
  const [rebootLines, setRebootLines] = useState([]);
  const [rebootReason, setRebootReason] = useState(null);

  const triggerMicroReboot = useCallback((reason = null) => {
    // Si une alerte est active, ne pas lancer le reboot
    if (getIsAlertActive()) {
      console.log('Micro reboot blocked - alert active');
      return;
    }
    
    setIsRebooting(true);
    setRebootLines([]);
    
    // Choisir une raison aléatoire si non spécifiée
    const selectedReason = reason === undefined 
      ? REBOOT_REASONS[Math.floor(Math.random() * REBOOT_REASONS.length)]
      : reason;
    setRebootReason(selectedReason);
    
    // Jouer les lignes séquentiellement
    let totalDelay = selectedReason ? 800 : 300; // Délai initial plus long si raison affichée
    
    REBOOT_LINES.forEach((line, index) => {
      setTimeout(() => {
        setRebootLines(prev => [...prev, line]);
      }, totalDelay);
      totalDelay += line.delay;
    });
    
    // Terminer le reboot
    setTimeout(() => {
      setIsRebooting(false);
      setRebootLines([]);
      setRebootReason(null);
    }, totalDelay + 500);
  }, []);

  return { isRebooting, rebootLines, rebootReason, triggerMicroReboot };
};

// Composant d'affichage du micro reboot
export const MicroRebootOverlay = ({ isRebooting, rebootLines, rebootReason }) => {
  if (!isRebooting) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/95 backdrop-blur-sm">
      <div 
        className="w-full max-w-2xl mx-4 p-6 bg-black border-2 border-cyan-500 rounded"
        style={{ boxShadow: '0 0 30px rgba(0, 240, 255, 0.3)' }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-4 pb-3 border-b border-cyan-500/50">
          <RotateCcw className="w-6 h-6 text-cyan-400 animate-spin" />
          <span className="text-cyan-400 font-mono font-bold text-lg">MICRO REBOOT EN COURS</span>
        </div>
        
        {/* Raison du reboot si présente */}
        {rebootReason && (
          <div className="mb-4 px-3 py-2 bg-yellow-900/30 border border-yellow-500/50 rounded">
            <span className="text-yellow-400 font-mono text-sm">
              RAISON: {rebootReason}
            </span>
          </div>
        )}
        
        {/* Terminal de sortie */}
        <div className="font-mono text-sm bg-black/50 p-4 rounded border border-gray-700 min-h-[200px] max-h-[300px] overflow-y-auto">
          {rebootLines.map((line, index) => (
            <div 
              key={index} 
              className="mb-1"
              style={{ color: line.color || '#666' }}
            >
              {line.text || '\u00A0'}
            </div>
          ))}
          <span className="inline-block w-2 h-4 bg-cyan-400 animate-pulse" />
        </div>
        
        {/* Footer */}
        <div className="mt-4 pt-3 border-t border-cyan-500/30 text-center">
          <span className="text-gray-500 font-mono text-xs">
            HAL 3000 | hal3000.fr
          </span>
        </div>
      </div>
    </div>
  );
};

export default { useRandomAlerts, AlertPopup, useMicroReboot, MicroRebootOverlay };
